// =============================================================================
// ui/tabs/ResultsTiming.tsx — the Results board's "Timing" card. #58.
// -----------------------------------------------------------------------------
// One win-rate bar per CME session, in beta's fixed clock order (Asia, EU,
// NY-AM, NY-PM), under a sentence naming the strongest and weakest session.
// Ported from `app/backtest-dashboard.html:292-751` (`renderSessions` plus the
// card's own markup) — the eyebrow, the heading, the note and the
// not-enough-samples sentence are that file's strings, unchanged.
//
// ── 🚨 THE CALLOUT IS THE ONE LINE HERE THAT CAN COST MONEY ──
//
// The bars report; the callout ADVISES — "trade the NY-AM open, skip Asia" is
// what a trader takes away from it. A session with three samples can top the
// list on luck alone, so only sessions with at least `minSample` samples may be
// named, and when none clear that floor the card says so outright instead of
// crowning the best of a bad set. That gate is beta's (`n >= 5`, now the
// caller's number) and it is the reason this card is safe to read.
//
// ── ⚠ THE CALLOUT RANKS BY EXPECTANCY, THE BARS SHOW WIN RATE ──
//
// So the longest bar is NOT necessarily the session the callout calls
// strongest, and that is not a bug: a 40%-win session of big winners beats a
// 70%-win session of scratches, and expectancy is the number that says so.
// Reproduced deliberately from beta. If it ever reads as a contradiction on
// screen, the fix is to label the callout's number, not to re-rank it.
//
// ── ⚠ A REAL GAP, REPRODUCED ON PURPOSE — SESSIONLESS SAMPLES VANISH ──
//
// Beta buckets by exact match against its fixed session list, so a sample saved
// with no session (the Backtesting tab's "Auto" option) matches nothing and
// silently falls out of this card. Consequence: THESE ROWS DO NOT SUM TO THE
// BOOK — the sample counts here can total less than the Samples tile above, with
// nothing on screen explaining the shortfall. The selector reproduces the same
// drop (`sessionGroups`, which is where a fix would go).
//
// Left alone because closing it is a behaviour change on a parity port, not
// because it is right. Worth a decision later: either an "Unassigned" row, or a
// note on the card saying how many samples are unbucketed.
//
// ── ⚠ COPY: THE HEADING IS TWO ELEMENTS IN BETA, NOT ONE ──
//
// The rendered page reads "When the edge shows up by CME session (ET)" as one
// line, but the source is an `<h3>` plus a smaller `<span class="note">`. Kept
// as two, because collapsing them would drop the size/colour distinction the
// stylesheet relies on.
// =============================================================================

import type { BoardGroup } from '../../selectors/backtestBoard';
import { num, pct } from '../format';

/**
 * ⚠ Beta's floor for a bar that would otherwise be invisible: a 0% session
 * renders a 2%-wide sliver so the row still reads as a row rather than an empty
 * track. Presentation only — it never changes the number printed beside it.
 */
const MIN_BAR_PCT = 2;

/** The win rate at or above which the bar draws in the positive colour. Beta's. */
const POSITIVE_WIN_RATE = 50;

export interface ResultsTimingProps {
  /**
   * Already in beta's fixed session order and already filtered to sessions that
   * have samples — this component does not sort or drop rows.
   */
  groups: readonly BoardGroup[];
  /**
   * The floor below which a session is still DRAWN but may not be NAMED in the
   * callout. Beta hardcodes 5. See the header.
   */
  minSample: number;
}

export function ResultsTiming({ groups, minSample }: ResultsTimingProps) {
  // 🚨 The callout's candidate set, and the only place the sample floor is
  // applied. `expectancy == null` cannot happen for a group that has samples,
  // but the type allows it and an unrankable session must not be named on a
  // guess — so it is excluded here rather than coerced to a number.
  const rankable = groups
    .flatMap((g) => (g.stats.n >= minSample && g.stats.expectancy != null
      ? [{ label: g.label, expectancy: g.stats.expectancy }]
      : []))
    .sort((a, b) => b.expectancy - a.expectancy);

  const best = rankable[0];
  // ⚠ Beta takes the worst from the SAME filtered set and then suppresses the
  // clause when it is the same session as the best — which is what happens when
  // exactly one session clears the floor. Kept: "strongest in NY-AM · weakest in
  // NY-AM" is a sentence that argues with itself.
  const worst = rankable.length > 1 ? rankable[rankable.length - 1] : undefined;

  return (
    <div className="bd-tm-card" data-testid="results-timing">
      <div className="bd-tm-head">
        <span className="bd-tm-eyebrow">Timing</span>
        <h3 className="bd-tm-title">When the edge shows up</h3>
        <span className="bd-tm-note">by CME session (ET)</span>
      </div>

      <div className="bd-tm-rows">
        {groups.map((g) => {
          const { n, winRate, net } = g.stats;
          // Trivial presentation maths: the bar's width IS the win rate, floored
          // so a zero still draws. A null win rate (nothing decided yet) is drawn
          // as the floor and printed as an em-dash by `pct`.
          const width = Math.max(MIN_BAR_PCT, winRate ?? 0);
          // ⚠ `winRate ?? 0` — in beta `null >= 50` is false, so an undecided
          // session gets the dim bar. Same outcome, stated rather than inherited
          // from a loose comparison.
          const strong = (winRate ?? 0) >= POSITIVE_WIN_RATE;

          return (
            <div className="bd-tm-row" key={g.key} data-testid="results-timing-row">
              <span className="bd-tm-name">{g.label}</span>
              <div className="bd-tm-track">
                <span
                  className={`bd-tm-fill${strong ? '' : ' bd-tm-fill-dim'}`}
                  style={{ width: `${width}%` }}
                />
              </div>
              {/* ⚠ The colour of this figure follows NET POINTS, not the win
                  rate beside it — beta's choice, and the sibling Confluence card
                  does the opposite. A session can win often and still bleed. */}
              <span className={`bd-tm-val ${net >= 0 ? 'bd-tm-pos' : 'bd-tm-neg'}`}>
                {pct(winRate, 0)}
                {' '}
                <small className="bd-tm-sub">
                  · {net >= 0 ? '+' : ''}{num(net, 1)} pts · n={n}
                </small>
              </span>
            </div>
          );
        })}
      </div>

      <div className="bd-tm-callout">
        {best ? (
          <>
            Strongest in <b className="bd-tm-strong">{best.label}</b>
            {' '}({best.expectancy >= 0 ? '+' : ''}{num(best.expectancy, 1)} pts/trade)
            {worst ? (
              <>
                {' '}· weakest in <b className="bd-tm-strong">{worst.label}</b>
                {' '}({worst.expectancy >= 0 ? '+' : ''}{num(worst.expectancy, 1)} pts/trade)
              </>
            ) : null}
            .
          </>
        ) : (
          // 🚨 Beta's exact sentence, and the whole point of the floor: with a
          // thin slice this card refuses to name a session at all rather than
          // pointing the trader at three lucky samples.
          'Not enough samples in this slice for a timing read.'
        )}
      </div>
    </div>
  );
}
